import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  TextField, 
  Paper, 
  Container, 
  Stack, 
  Chip, 
  Alert, 
  CircularProgress,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import { 
  ArrowBack, 
  SmartToy, 
  RocketLaunch, 
  Save, 
  Refresh, 
  Close, 
  CheckCircle,
  Cancel,
  AccessTime,
  Description
} from '@mui/icons-material';
import { apiCall } from '../utils/api';



const AITemplateCreator = ({ 
  onBack, 
  familyId, 
  onTemplateCreated,
  onViewExistingTemplate 
}) => {
  console.log('=== AITemplateCreator component rendering ===', { onBack, familyId, onTemplateCreated, onViewExistingTemplate });
  
  const [userInput, setUserInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingProgress, setPollingProgress] = useState('');
  const [costEstimate, setCostEstimate] = useState(null);
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [error, setError] = useState('');
  const [retryData, setRetryData] = useState(null);
  const [aiFeatureEnabled, setAiFeatureEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(0);

  const [hasSaveError, setHasSaveError] = useState(false);

  // Check AI feature status on component mount
  useEffect(() => {
    checkAiFeatureStatus();
  }, []);

  // Cleanup effect to prevent stuck states
  useEffect(() => {
    return () => {
      console.log('=== FRONTEND: Component unmounting, cleaning up save state ===');
      setIsSaving(false);
      setLastSaveTime(0);
    };
  }, []);

  // Effect to reset save state on errors
  useEffect(() => {
    if (error && isSaving) {
      console.log('=== FRONTEND: Error detected while saving, resetting save state ===');
      setIsSaving(false);
    }
  }, [error, isSaving]);

  const checkAiFeatureStatus = async () => {
    try {
      const response = await apiCall('/api/ai/health');
      setAiFeatureEnabled(response.data?.ai_feature_enabled || false);
    } catch (error) {
      console.error('Error checking AI feature status:', error);
      setAiFeatureEnabled(false);
    }
  };

  const handleInputChange = async (e) => {
    const value = e.target.value;
    setUserInput(value);
    
    // Only clear generation errors, not save errors
    if (!error || !error.includes('save template')) {
      setError('');
    }
    
    // Reset save error state when user starts typing
    if (hasSaveError) {
      setHasSaveError(false);
    }
    
    setRetryData(null);
    setPollingProgress('');

    // Get cost estimate when user types
    if (value.trim().length > 10) {
      try {
        const estimate = await apiCall('/api/ai/estimate-cost', {
          method: 'POST',
          body: JSON.stringify({ input: value })
        });
        setCostEstimate(estimate.data || estimate);
      } catch (error) {
        console.error('Error estimating cost:', error);
      }
    } else {
      setCostEstimate(null);
    }
  };

  const generateTemplate = async (forceSync = false) => {
    if (!userInput.trim()) {
      setError('Please describe what kind of runbook you need.');
      return;
    }

    if (!aiFeatureEnabled) {
      setError('AI template generation is currently disabled.');
      return;
    }

    setIsGenerating(true);
    setError('');
    setRetryData(null);

    const useAsyncMode = !forceSync;

    try {
      const requestData = {
        input: String(userInput).trim(),
        family_id: String(familyId),
        async: Boolean(useAsyncMode)
      };
      
      const response = await apiCall('/api/ai/generate-template', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (useAsyncMode && response.data?.job_id) {
        setIsGenerating(false);
        setIsPolling(true);
        setPollingProgress('Starting template generation...');
        await pollForResult(response.data.job_id);
      } else {
        const templateData = getTemplateData(response.data || response);
        
        if (!templateData || !isValidTemplate(templateData)) {
          setError('AI service returned invalid template data. Please try again.');
          setIsGenerating(false);
          return;
        }
        
        setGeneratedTemplate(response.data || response);
        setIsGenerating(false);
        
        if (window.navigator && window.navigator.vibrate) {
          window.navigator.vibrate(100);
        }
      }

    } catch (error) {
      console.error('Error generating template:', error);
      
      if (error.status === 408 || error.suggestion === 'retry_async') {
        if (!useAsyncMode) {
          setError('Request timed out. Retrying with background processing...');
          setRetryData({
            estimatedTime: '30-60 seconds',
            action: 'async'
          });
          setTimeout(() => generateTemplate(false), 1000);
          return;
        } else {
          setError('Unable to process your request at this time. Please check your connection and try again later.');
          setRetryData({
            action: 'retry'
          });
        }
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('CORS')) {
        setError('Network connection issue. Please check your internet connection and try again.');
      } else if (error.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else if (error.status === 500) {
        setError('AI service is experiencing issues. Please try again later.');
      } else {
        setError(error.message || 'An unexpected error occurred. Please try again.');
      }
      
      setIsGenerating(false);
    }
  };

  const pollForResult = async (jobId) => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setError('Template generation is taking longer than expected. Please check back later.');
        setIsPolling(false);
        setPollingProgress('');
        return;
      }
      
      try {
        const response = await apiCall(`/api/ai/job-status/${jobId}`);
        
        if (response.data?.status === 'completed' && response.data?.result) {
          const templateData = getTemplateData(response.data.result);
          
          if (!templateData || !isValidTemplate(templateData)) {
            setError('Generated template is incomplete. Please try again with different input.');
            setIsPolling(false);
            setPollingProgress('');
            return;
          }
          
          setGeneratedTemplate(response.data.result);
          setIsPolling(false);
          setPollingProgress('');
          
          if (window.navigator && window.navigator.vibrate) {
            window.navigator.vibrate(100);
          }
        } else if (response.data?.status === 'failed') {
          setError('Template generation failed. Please try again.');
          setIsPolling(false);
          setPollingProgress('');
        } else {
          attempts++;
          const progress = response.data?.progress || `Processing... (${attempts}/${maxAttempts})`;
          setPollingProgress(progress);
          setTimeout(poll, 5000);
        }
      } catch (error) {
        console.error('Error polling for result:', error);
        setError('Error checking template generation status. Please try again.');
        setIsPolling(false);
        setPollingProgress('');
      }
    };
    
    poll();
  };

  const getTemplateData = (data) => {
    try {
      if (data?.template) {
        return data.template;
      } else if (data?.data?.template) {
        return data.data.template;
      } else if (data?.steps && data?.title) {
        return data;
      } else if (data?.data?.steps && data?.data?.title) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error in getTemplateData:', error);
      return null;
    }
  };

  const isValidTemplate = (template) => {
    try {
      if (!template || typeof template !== 'object') {
        return false;
      }
      
      const requiredFields = ['title', 'description', 'steps'];
      const missingFields = requiredFields.filter(field => !template[field]);
      
      if (missingFields.length > 0) {
        return false;
      }
      
      if (!Array.isArray(template.steps) || template.steps.length === 0) {
        return false;
      }
      
      for (let i = 0; i < template.steps.length; i++) {
        const step = template.steps[i];
        if (!step || typeof step !== 'object') {
          return false;
        }
        
        if (!step.title || !step.description) {
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error validating template:', error);
      return false;
    }
  };

  const clearError = () => {
    setError('');
    setRetryData(null);
  };

  const handleViewExistingTemplate = (templateTitle) => {
    if (onViewExistingTemplate) {
      onViewExistingTemplate(templateTitle);
    }
  };

  const saveTemplate = async () => {
    if (!generatedTemplate) {
      setError('No template to save.');
      return;
    }

    if (!familyId) {
      setError('Family ID is required to save template.');
      return;
    }

    const now = Date.now();
    if (now - lastSaveTime < 2000) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSaveSuccess(false);
    setHasSaveError(false);
    setLastSaveTime(now);
    

    try {
      const templateData = getTemplateData(generatedTemplate);
      
      if (!templateData) {
        throw new Error('Invalid template data structure');
      }

      const saveData = {
        title: templateData.title,
        description: templateData.description,
        category: 'General', // Default category for AI-generated templates
        steps: templateData.steps.map(step => ({
          title: step.title,
          description: step.description,
          step_number: step.step_number || '',
          estimated_time: step.estimated_time || '',
          notes: step.notes || '',
          critical: Boolean(step.critical)
        })),
        family_id: familyId,
        template_type: 'ai_generated'
      };

      console.log('=== FRONTEND: About to save template ===');
      console.log('Save data:', saveData);
      
      const response = await apiCall('/api/templates', {
        method: 'POST',
        body: JSON.stringify(saveData)
      });

      console.log('=== FRONTEND: Save response received ===');
      console.log('Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response));

      // Check if response has an id field (indicating successful creation)
      if (response && response.id) {
        setSaveSuccess(true);
        setError('');
        setHasSaveError(false);
        
        if (window.navigator && window.navigator.vibrate) {
          window.navigator.vibrate(100);
        }
        
        if (onTemplateCreated) {
          onTemplateCreated(response);
        }
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error) {
      console.error('Error saving template:', error);
      
      let errorMessage = 'Failed to save template.';
      
      if (error.status === 400) {
        errorMessage = 'Template data is invalid. Please try again.';
      } else if (error.status === 401) {
        errorMessage = 'You are not authorized to save templates in this family.';
      } else if (error.status === 403) {
        errorMessage = 'You do not have permission to save templates in this family.';
      } else if (error.status === 409) {
        errorMessage = 'A template with this title already exists. Please use a different title.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(`Save template: ${errorMessage}`);
      setHasSaveError(true);
      setSaveSuccess(false);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setUserInput('');
    setGeneratedTemplate(null);
    setError('');
    setRetryData(null);
    setPollingProgress('');
    setCostEstimate(null);
    setSaveSuccess(false);
    setHasSaveError(false);
    setLastSaveTime(0);
  };

  const handleRetry = () => {
    if (retryData?.action === 'async') {
      generateTemplate(false);
    } else {
      generateTemplate(true);
    }
    setRetryData(null);
  };

  const cancelGeneration = () => {
    setIsGenerating(false);
    setIsPolling(false);
    setPollingProgress('');
    setError('');
    setRetryData(null);
  };

  const isOperationInProgress = isGenerating || isPolling || isSaving;

  // Error boundary for unexpected errors
  if (hasError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center">
            <Typography variant="h4" component="h1" color="error.main">
              🤖 AI Template Generator
            </Typography>
            <Typography variant="h6" component="h2">
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary">
              An unexpected error occurred. Please try refreshing the page or go back and try again.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => setHasError(false)}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={onBack}
              >
                Go Back
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (!aiFeatureEnabled) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center">
            <Typography variant="h4" component="h1" color="error.main">
              🤖 AI Template Generation Unavailable
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This feature is currently disabled. Please contact your administrator or try creating a template manually.
            </Typography>
            <Button onClick={onBack} variant="outlined">
              Go Back
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Template renderer component
  const TemplateRenderer = ({ template }) => {
    try {
      const templateData = getTemplateData(template);
      
      if (!templateData) {
        return (
          <Alert severity="error">
            Unable to extract template data from AI response
          </Alert>
        );
      }
      
      if (!isValidTemplate(templateData)) {
        return (
          <Alert severity="error">
            Template structure is invalid. Missing required fields.
          </Alert>
        );
      }
      
      const safeText = (value) => {
        try {
          if (value === null || value === undefined) return '';
          if (typeof value === 'string') return value;
          if (typeof value === 'number') return String(value);
          if (typeof value === 'boolean') return String(value);
          if (typeof value === 'object') {
            try {
              return JSON.stringify(value);
            } catch {
              return '[Object]';
            }
          }
          return String(value);
        } catch (error) {
          console.error('Error in safeText:', error, 'Value:', value);
          return '[Error]';
        }
      };
      
      let stepsArray = [];
      if (Array.isArray(templateData.steps)) {
        stepsArray = templateData.steps.filter(step => {
          if (!step || typeof step !== 'object') {
            return false;
          }
          return true;
        });
      }
      
      const validatedSteps = stepsArray.map((step, index) => {
        try {
          const validatedStep = {
            title: safeText(step.title || ''),
            description: safeText(step.step_description || step.description || ''),
            step_number: safeText(step.step_number || String(index + 1)),
            estimated_time: step.estimated_time ? safeText(step.estimated_time) : null,
            notes: step.notes ? safeText(step.notes) : null,
            critical: Boolean(step.critical)
          };
          
          return validatedStep;
        } catch (error) {
          console.error(`Error validating step ${index}:`, error);
          return {
            title: 'Error in step',
            description: 'This step could not be processed',
            step_number: String(index + 1),
            estimated_time: null,
            notes: null,
            critical: false
          };
        }
      });

      return (
        <Box>
          <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
            {safeText(templateData.title)}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {safeText(templateData.description)}
          </Typography>
          
          <Typography variant="h6" component="h4" sx={{ mb: 2, fontWeight: 600 }}>
            Steps:
          </Typography>
          {validatedSteps.map((step, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                  <Chip 
                    label={step.step_number} 
                    color="primary" 
                    size="small"
                  />
                  <Typography variant="h6" component="h5" sx={{ fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  {step.critical && (
                    <Chip 
                      label="Critical" 
                      color="error" 
                      size="small"
                    />
                  )}
                </Stack>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {step.description}
                </Typography>
                {step.estimated_time && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" />
                    Estimated time: {step.estimated_time}
                  </Typography>
                )}
                {step.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Description fontSize="small" />
                    Notes: {step.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    } catch (error) {
      console.error('Error in TemplateRenderer:', error);
      return (
        <Alert severity="error">
          Error rendering template: {error.message}
        </Alert>
      );
    }
  };

  try {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToy sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                AI Template Generator
              </Typography>
            </Box>
            <Chip 
              label="Powered by AWS Bedrock" 
              color="primary" 
              variant="outlined"
              size="small"
            />
          </Stack>

          {/* Error banner */}
          {error && (
            <Alert 
              severity={error.includes('already exists') ? 'warning' : 'error'}
              sx={{ mb: 3 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={clearError}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Error:</strong> {error}
              </Typography>
              {error.includes('already exists') && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    const templateData = getTemplateData(generatedTemplate);
                    const templateTitle = templateData?.title || 'Unknown Template';
                    handleViewExistingTemplate(templateTitle);
                  }}
                  sx={{ p: 0, minWidth: 'auto', textDecoration: 'underline' }}
                >
                  Click here to view the existing template
                </Button>
              )}
            </Alert>
          )}

          {!generatedTemplate ? (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                    Describe Your Runbook
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Tell me what kind of emergency procedure or step-by-step guide you need. Be as specific as possible.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Examples:
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                      <Chip
                        label="Create a runbook for handling a home water leak emergency"
                        onClick={() => setUserInput("Create a runbook for handling a home water leak emergency")}
                        variant="outlined"
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      />
                      <Chip
                        label="I need steps for changing a flat tire on the highway"
                        onClick={() => setUserInput("I need steps for changing a flat tire on the highway")}
                        variant="outlined"
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      />
                      <Chip
                        label="Help me create a guide for power outage preparation"
                        onClick={() => setUserInput("Help me create a guide for power outage preparation")}
                        variant="outlined"
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      />
                    </Stack>
                  </Box>
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={userInput}
                    onChange={handleInputChange}
                    placeholder="Describe the runbook you need in plain English..."
                    disabled={isOperationInProgress}
                    inputProps={{ maxLength: 2000 }}
                    sx={{ mb: 2 }}
                  />
                  
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {userInput.length}/2000 characters
                    </Typography>
                    
                    {costEstimate && costEstimate.estimated_cost !== undefined && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">
                          Estimated cost: <strong>${Number(costEstimate.estimated_cost).toFixed(4)}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({(costEstimate.estimated_input_tokens || 0) + (costEstimate.estimated_output_tokens || 0)} tokens)
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 2 }}
                    action={
                      retryData && (
                        <Button
                          size="small"
                          onClick={handleRetry}
                          startIcon={<Refresh />}
                          variant="outlined"
                        >
                          {retryData.action === 'async' 
                            ? `Try Async Mode${retryData.estimatedTime ? ` (${retryData.estimatedTime})` : ''}` 
                            : 'Retry'
                          }
                        </Button>
                      )
                    }
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 1 }}>
                      {error}
                    </Typography>
                    {retryData && retryData.action === 'async' && (
                      <Typography variant="caption" color="text.secondary">
                        Async mode processes your request in the background to avoid timeouts.
                      </Typography>
                    )}
                  </Alert>
                )}
                
                <Box sx={{ textAlign: 'center' }}>
                  {isOperationInProgress ? (
                    <Stack spacing={2} alignItems="center">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={cancelGeneration}
                        startIcon={<Cancel />}
                        size="large"
                      >
                        Cancel Generation
                      </Button>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body1">
                          {isGenerating ? 'Starting Generation...' : (pollingProgress || 'Processing...')}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => generateTemplate()}
                      disabled={!userInput.trim()}
                      startIcon={<RocketLaunch />}
                      sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
                    >
                      Generate Template
                    </Button>
                  )}
                </Box>
              </Stack>
            </Paper>
          ) : (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                    ✅ Template Generated Successfully!
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your AI-generated runbook template is ready. Review it below and save it to your templates.
                  </Typography>
                </Box>

                <Card variant="outlined">
                  <CardContent>
                    <TemplateRenderer template={generatedTemplate} />
                  </CardContent>
                </Card>

                <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    startIcon={<ArrowBack />}
                  >
                    Create Another
                  </Button>
                  {saveSuccess ? (
                    <Alert severity="success" icon={<CheckCircle />}>
                      Template saved successfully!
                    </Alert>
                  ) : (
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        onClick={saveTemplate}
                        disabled={isSaving || (Date.now() - lastSaveTime < 2000) || hasSaveError}
                        startIcon={<Save />}
                        title={
                          hasSaveError 
                            ? 'Please fix the error before saving again' 
                            : Date.now() - lastSaveTime < 2000 
                              ? 'Please wait before saving again' 
                              : 'Save template to your library'
                        }
                      >
                        {isSaving ? 'Saving...' : 
                         hasSaveError ? 'Fix Error First' :
                         'Save Template'}
                      </Button>
                      {isSaving && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setIsSaving(false);
                            setLastSaveTime(0);
                          }}
                          startIcon={<Refresh />}
                          title="Reset save state if stuck"
                        >
                          Reset
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Paper>
          )}
        </Paper>
      </Container>
    );
  } catch (error) {
    console.error('Unexpected error in AITemplateCreator render:', error);
    setHasError(true);
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center">
            <Typography variant="h4" component="h1" color="error.main">
              🤖 AI Template Generator
            </Typography>
            <Typography variant="h6" component="h2">
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary">
              An unexpected error occurred while rendering the component.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => setHasError(false)}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={onBack}
              >
                Go Back
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    );
  }
};

export default AITemplateCreator;
